import api from '../api';

/**
 * Export utility functions for the admin panel
 */

/**
 * Trigger a food export with the given configuration
 * @param {Object} exportConfig Export configuration
 * @param {string[]} exportConfig.fields Selected fields to export
 * @param {string[]} exportConfig.languages Selected languages for multi-language export
 * @param {Object[]} exportConfig.filters Applied filters
 * @param {string} exportConfig.format Export format (csv, excel, json)
 * @param {Object} exportConfig.config Additional configuration
 * @returns {Promise<Object>} Export result with download information
 */
export const exportFoodData = async (exportConfig) => {
  try {
    const response = await api.post('/export/foods', exportConfig);
    return response.data;
  } catch (error) {
    console.error('Export failed:', error);
    throw new Error(error.response?.data?.message || 'Export failed');
  }
};

/**
 * Get available fields for export
 * @returns {Promise<Object>} Available fields organized by category
 */
export const getExportFields = async () => {
  try {
    const response = await api.get('/export/fields');
    return response.data.data;
  } catch (error) {
    console.error('Failed to get export fields:', error);
    throw new Error('Failed to load export fields');
  }
};

/**
 * Generate a preview of the export data
 * @param {Object} previewConfig Preview configuration
 * @returns {Promise<Object>} Preview data
 */
export const generateExportPreview = async (previewConfig) => {
  try {
    const response = await api.post('/export/preview', previewConfig);
    return response.data.data;
  } catch (error) {
    console.error('Preview generation failed:', error);
    throw new Error(error.response?.data?.message || 'Preview generation failed');
  }
};

/**
 * Download an export file by export ID
 * @param {string} exportId The export ID
 * @param {string} filename The filename for download
 */
export const downloadExportFile = async (exportId, filename) => {
  try {
    const response = await api.get(`/export/download/${exportId}`, {
      responseType: 'blob'
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Download failed:', error);
    throw new Error(error.response?.data?.message || 'Download failed');
  }
};

/**
 * Validate export configuration
 * @param {Object} config Export configuration
 * @returns {Object} Validation result with errors
 */
export const validateExportConfig = (config) => {
  const errors = {};

  // Validate fields
  if (!config.fields || config.fields.length === 0) {
    errors.fields = 'At least one field must be selected';
  }

  // Validate format
  if (!config.format || !['csv', 'excel', 'json'].includes(config.format)) {
    errors.format = 'Invalid export format';
  }

  // Validate filename
  if (!config.config?.filename || config.config.filename.trim() === '') {
    errors.filename = 'Filename is required';
  } else if (!/^[a-zA-Z0-9_-]+$/.test(config.config.filename)) {
    errors.filename = 'Filename can only contain letters, numbers, underscores, and hyphens';
  }

  // Validate max rows
  if (config.config?.maxRows && (config.config.maxRows < 1 || config.config.maxRows > 100000)) {
    errors.maxRows = 'Max rows must be between 1 and 100,000';
  }

  // Validate languages
  if (!config.languages || config.languages.length === 0) {
    errors.languages = 'At least one language must be selected';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format filter for display
 * @param {Object} filter Filter object
 * @param {Object} availableFields Available fields with metadata
 * @returns {string} Formatted filter string
 */
export const formatFilterForDisplay = (filter, availableFields) => {
  const field = availableFields[filter.field];
  const fieldLabel = field?.label || filter.field;
  
  const operatorLabels = {
    contains: 'contains',
    equals: 'equals',
    starts_with: 'starts with',
    ends_with: 'ends with',
    not_contains: 'does not contain',
    not_equals: 'does not equal',
    greater_than: 'greater than',
    less_than: 'less than',
    greater_equal: 'greater than or equal to',
    less_equal: 'less than or equal to',
    between: 'between',
    contains_any: 'contains any of',
    contains_all: 'contains all of'
  };

  const operatorLabel = operatorLabels[filter.operator] || filter.operator;
  
  let valueStr = '';
  if (filter.operator === 'between') {
    valueStr = `${filter.value} and ${filter.value2}`;
  } else if (Array.isArray(filter.value)) {
    valueStr = filter.value.join(', ');
  } else if (typeof filter.value === 'boolean') {
    valueStr = filter.value ? 'Yes' : 'No';
  } else {
    valueStr = String(filter.value);
  }

  return `${fieldLabel} ${operatorLabel} ${valueStr}`;
};

/**
 * Get export summary text
 * @param {Object} config Export configuration
 * @param {Object} availableFields Available fields
 * @returns {string} Export summary
 */
export const getExportSummary = (config, availableFields) => {
  const { fields, languages, filters, format } = config;
  
  const parts = [];
  
  // Fields summary
  parts.push(`${fields.length} field${fields.length !== 1 ? 's' : ''}`);
  
  // Languages summary
  if (languages.length > 1) {
    parts.push(`${languages.length} language${languages.length !== 1 ? 's' : ''}`);
  }
  
  // Filters summary
  if (filters.length > 0) {
    parts.push(`${filters.length} filter${filters.length !== 1 ? 's' : ''}`);
  }
  
  // Format
  parts.push(`${format.toUpperCase()} format`);
  
  return `Export ${parts.join(', ')}`;
};

/**
 * Clean up temporary export files (for admin use)
 */
export const cleanupExportFiles = async () => {
  try {
    const response = await api.get('/export/cleanup');
    return response.data;
  } catch (error) {
    console.error('Cleanup failed:', error);
    throw new Error('Failed to cleanup export files');
  }
};

/**
 * Get file size in human readable format
 * @param {number} bytes File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Estimate export file size based on configuration
 * @param {Object} config Export configuration
 * @param {number} estimatedRows Estimated number of rows
 * @returns {string} Estimated file size
 */
export const estimateFileSize = (config, estimatedRows) => {
  const { fields, languages, format } = config;
  
  // Rough estimates (bytes per cell)
  const bytesPerCell = {
    csv: 20,
    excel: 15,
    json: 25
  };
  
  const cellsPerRow = fields.length + (languages.length - 1) * 2; // Extra cells for language fields
  const totalCells = estimatedRows * cellsPerRow;
  const estimatedBytes = totalCells * (bytesPerCell[format] || 20);
  
  return formatFileSize(estimatedBytes);
};

/**
 * Check if export configuration has changed
 * @param {Object} config1 First configuration
 * @param {Object} config2 Second configuration
 * @returns {boolean} True if configurations are different
 */
export const hasConfigChanged = (config1, config2) => {
  if (!config1 || !config2) return true;
  
  return JSON.stringify(config1) !== JSON.stringify(config2);
};

export default {
  exportFoodData,
  getExportFields,
  generateExportPreview,
  downloadExportFile,
  validateExportConfig,
  formatFilterForDisplay,
  getExportSummary,
  cleanupExportFiles,
  formatFileSize,
  estimateFileSize,
  hasConfigChanged
};